import React from 'react';
import {
  Box,
  Circle,
  Flex,
  FlexProps,
  Heading,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text
} from '@chakra-ui/react';
import { CloseIcon, VerticalEllipsisIcon } from '../../icons/index';
import {
  PossibleEnrichments,
  useCurrentExpert,
  useIsFindingExpert,
  useVendor
} from '@outdoorly/react-chat-sdk';
import { activatorVisibleKey, Store } from '../../store';
import { tryToBuildImgixUrl } from '../../utils/imgix';
import { useIsDesktop } from '../../utils/dimensions';
import posthog from 'posthog-js';
import { MenuItemPoweredByRemark, PoweredByRemark } from '../PoweredByRemark';

export const closeWidget = () => {
  Store.update((s) => {
    s.activatorVisible = true;
  });
  localStorage.setItem(activatorVisibleKey, 'visible');
  // TODO, delete after 1/27/22
  localStorage.setItem('isActivatorVisible', 'visible');
};
type ChatHeaderProps = {
  hideButtons?: boolean;
  expertProfile?: NonNullable<PossibleEnrichments['expert']>['expertProfile'];
};
export const ChatHeader: React.FC<ChatHeaderProps & FlexProps> = React.memo((props) => {
  const { hideButtons, expertProfile, ...parentProps } = props;
  const labels = Store.useState((s) => s.labels);

  const { vendor } = useVendor();

  const muted = Store.useState((s) => s.muted);
  const forcefullyShowFindingExpert = Store.useState((s) => s.forcefullyShowFindingExpert);
  // const {sessionInfo, initialized} = useSessionInfo()
  // const expertProfile = initialized ? client.getExpertProfile(sessionInf) : null
  const { currentExpert, initialized } = useCurrentExpert();

  const avatarSource = expertProfile ? expertProfile.avatar : currentExpert?.avatar ?? vendor?.logo;
  const avatarUrl = avatarSource
    ? tryToBuildImgixUrl(avatarSource, {
        w: 50,
        h: 50,
        auto: 'format,compress',
        crop: 'faces',
        fit: 'crop'
      })
    : undefined;

  const isDesktop = useIsDesktop();
  const isMobile = !isDesktop;

  const isFindingExpert = useIsFindingExpert();
  const header = expertProfile
    ? expertProfile.publicName
    : initialized && currentExpert && !forcefullyShowFindingExpert
    ? currentExpert.publicName
    : vendor?.name || '...';

  return (
    <Flex
      align="center"
      justify="space-between"
      background="scheme.whiteCompatible"
      borderRadius={isMobile ? 0 : '8px 8px 0 0'}
      as="header"
      p="9px 8px"
      {...parentProps}
    >
      <Flex align="center">
        <Box pos="relative" mr="14px" ml="4px">
          {(expertProfile || currentExpert) && vendor?.logo && !forcefullyShowFindingExpert ? (
            <Box pos="relative">
              <Image
                src={avatarUrl}
                width={'40px'}
                height={'40px'}
                borderRadius="50%"
                objectFit="cover"
                role="presentation"
                filter="drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.06))"
                border="4px solid rgba(255, 255, 255, 0.2)"
                boxSizing="content-box"
              />
              <Image
                src={vendor.logo}
                width={'16x'}
                height={'16px'}
                borderRadius="50%"
                objectFit="cover"
                role="presentation"
                filter="drop-shadow(0px 2px 24px rgba(0, 0, 0, 0.16))"
                border="2px solid rgba(255, 255, 255, 0.2)"
                position="absolute"
                boxSizing="content-box"
                bottom="2px"
                right="-6px"
              />
            </Box>
          ) : (!currentExpert || forcefullyShowFindingExpert) && vendor?.logo ? (
            <Image
              src={vendor.logo}
              width={'40px'}
              height={'40px'}
              borderRadius="50%"
              objectFit="cover"
              role="presentation"
              filter="drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.06))"
              border="4px solid rgba(255, 255, 255, 0.2)"
              boxSizing="content-box"
            />
          ) : (
            <Circle size="48px" opacity={0.2} background="white" />
          )}
        </Box>
        <Flex flexDir="column">
          <Heading
            variant="heading-medium"
            color="scheme.contentInverted"
            as="h1"
            aria-label={initialized && currentExpert ? `Chat with ${header}` : `Start a chat on ${header}`}
          >
            {header}
          </Heading>
          {(isFindingExpert || (initialized && currentExpert)) && (
            <Text variant="label-medium" color="scheme.contentInverseTransparent75" mt="4px" noOfLines={1}>
              {initialized && currentExpert && !forcefullyShowFindingExpert
                ? currentExpert.tagline
                : labels.searchingForExpert}
            </Text>
          )}
        </Flex>
      </Flex>

      {!hideButtons && (
        <Flex>
          <Menu variant="chat-header">
            <MenuButton
              w="32px"
              h="32px"
              minW="32px"
              icon={
                <VerticalEllipsisIcon
                  fill="scheme.contentInverseTransparent75"
                  color="scheme.contentInverseTransparent75"
                  width="4px"
                  height="16px"
                />
              }
              as={IconButton}
              aria-label="open chat menu"
              mr="8px"
              backgroundColor="transparent"
              _hover={{
                background: 'transparent',
                '& > svg': {
                  color: 'scheme.contentInverted',
                  fill: 'scheme.contentInverted'
                }
              }}
              _focus={{ outline: 0 }}
              _focusVisible={{
                outline: '1px solid',
                outlineColor: 'scheme.contentInverted'
              }}
              _active={{
                opacity: 1,
                '& > svg': {
                  color: 'scheme.contentInverted',
                  fill: 'scheme.contentInverted',
                  fillOpacity: 1
                }
              }}
            />
            <MenuList borderColor="scheme.borderPrimary" zIndex="popover">
              <MenuItem
                onClick={() => {
                  posthog.capture('MUTE-CHAT');
                  Store.update((s) => {
                    s.muted = !muted;
                  });
                  localStorage.setItem('remark-mutedChat', String(!muted ? 'muted' : ''));
                  // TODO, delete after 1/27/22, replace getItem with remark-mutedChat key
                  localStorage.setItem('mutedChat', String(!muted ? 'muted' : ''));
                }}
              >
                {muted ? 'Unmute' : 'Mute'}
              </MenuItem>

              <MenuItemPoweredByRemark />
            </MenuList>
          </Menu>
          <IconButton
            backgroundColor="transparent"
            w="32px"
            h="32px"
            minW="32px"
            mr="5px"
            icon={
              <CloseIcon
                fill="scheme.contentInverseTransparent75"
                color="scheme.contentInverseTransparent75"
                w="16px"
                h="16px"
              />
            }
            aria-label="close chat"
            _hover={{
              background: 'transparent',
              '& > svg': {
                color: 'scheme.contentInverted',
                fill: 'scheme.contentInverted'
              }
            }}
            _focus={{ outline: 0 }}
            _focusVisible={{
              outline: '1px solid',
              outlineColor: 'scheme.contentInverted'
            }}
            _active={{
              opacity: 1,
              '& > svg': {
                color: 'scheme.contentInverted',
                fill: 'scheme.contentInverted',
                fillOpacity: 1
              }
            }}
            onClick={() => {
              posthog.capture('CLOSE-CHAT-FROM-HEADER');
              closeWidget();
            }}
          />
        </Flex>
      )}
    </Flex>
  );
});
